// MasterFileModal — Feb 2026 (Randy pt89b). Big pop-up that hosts the
// "Create 'Send to Silo' Master File" flow. Not a calendar plan — this
// creates a RAG-formatted Master Overview document (chores + household
// details + members + exemptions + schedules + working agreements) that
// the user then pushes into their silo's Tier 1, 2 or 3 memory via the
// shared DocSaveDialog (same UX as every other Push-to-silo in My Silo).
//
// The user can EDIT the AI-generated text before pushing; they can
// re-generate any time (that regenerates fresh, doesn't append).
import { useState } from "react";
import { X, Sparkle, PaperPlaneRight, Spinner, ArrowsClockwise, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import DocSaveDialog from "@/components/DocSaveDialog";

export default function MasterFileModal({ siloId, siloName, siloKind, onClose }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [pushDialog, setPushDialog] = useState(false);
  const defaultTier = (siloKind === "personal" || siloKind === "organizer") ? 1 : 2;

  const generate = async () => {
    setBusy(true);
    try {
      // Feb 2026 (Randy pt89c) — this endpoint now returns a job_id and
      // runs the LLM in a background task, then we poll every 2s until
      // status = "done" | "error". Fixes the Cloudflare 502 that killed
      // the response for large silos where the LLM took >30s.
      const r = await api.post(`/calendar/${siloId}/chores/master-file/generate`, {});
      const jobId = r.data?.job_id;
      if (!jobId) {
        // Older sync shape kept as fallback during rollout.
        const t = (r.data?.text || "").trim();
        if (!t) toast.error("The AI returned an empty file — try again.");
        else { setText(t); toast.success("Master file drafted — review, edit, then Push to silo."); }
        return;
      }
      let out = "";
      let done = false;
      let errMsg = null;
      for (let i = 0; i < 60; i += 1) {
        await new Promise((res) => setTimeout(res, 2000));
        try {
          const pr = await api.get(`/calendar/master-file/job/${jobId}`);
          const st = pr.data?.status;
          if (st === "done") { out = (pr.data?.text || "").trim(); done = true; break; }
          if (st === "error") { errMsg = pr.data?.error || "Generation failed."; done = true; break; }
        } catch { /* transient — keep polling */ }
      }
      if (!done) { toast.error("The Master File is taking longer than expected — try again in a moment."); return; }
      if (errMsg) { toast.error(errMsg); return; }
      if (!out) toast.error("The AI returned an empty file — try again.");
      else { setText(out); toast.success("Master file drafted — review, edit, then Push to silo."); }
    } catch (e) {
      if (e.response?.status === 402 && e.response?.data?.guard_code === "LLM_GUARD") return;
      toast.error(e.response?.data?.detail || "Couldn't generate.");
    } finally { setBusy(false); }
  };

  const pushToSilo = () => {
    if (!text.trim()) { toast.error("Nothing to push — generate the file first."); return; }
    setPushDialog(true);
  };

  const today = new Date();
  const defaultTitle = `Chores — Master overview (${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,"0")}-${String(today.getDate()).padStart(2,"0")})`;

  return (
    <div className="fixed inset-0 z-[220] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FDFCF8] rounded-2xl w-full max-w-2xl max-h-[92dvh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="master-file-modal">
        <div className="px-5 py-3 border-b hairline flex items-center justify-between shrink-0">
          <h2 className="font-serif-display text-lg text-[#1C1B1A] flex items-center gap-2">
            <FileText size={17} weight="duotone" className="text-[#4A5D4E]" />
            &quot;Send to Silo&quot; Master File
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#F0F1EE]" data-testid="master-file-close"><X size={18} /></button>
        </div>

        <div className="px-5 pt-3 pb-2 shrink-0">
          <p className="text-[11px] text-[#6B6A68] leading-relaxed">
            This creates a <strong>Master Overview document</strong> of your household&apos;s chore setup — designed to live INSIDE your silo (Tier 1 / 2 / 3), not on the calendar. The AI Assistant will use it to answer questions like &quot;who does the dishes on Wednesdays?&quot; or &quot;what&apos;s the standard for cleaning the bathroom?&quot; without having to re-parse every calendar event.
          </p>
        </div>

        <div className="px-5 pb-3 flex-1 overflow-y-auto min-h-[240px]">
          <textarea value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Tap Create Master File below and the AI will draft your household's chore overview here. It's fully editable — scroll through, tweak wording, delete anything that looks off, then Push to silo."
            rows={16} data-testid="master-file-textarea"
            className="w-full px-3 py-3 border hairline rounded-md text-sm bg-white focus:outline-none focus:border-[#4A5D4E] resize-y font-mono leading-relaxed" />
          <div className="text-[9px] text-[#B5B3AE] mt-0.5 text-right tabular-nums">{text.length} chars</div>
        </div>

        <div className="px-5 py-3 border-t hairline flex items-center gap-2 shrink-0 pb-[calc(0.75rem_+_env(safe-area-inset-bottom))] flex-wrap">
          <button onClick={generate} disabled={busy} data-testid="master-file-generate"
            className="text-sm px-4 py-2.5 rounded-md border border-[#C19A4A] text-[#8A6A24] bg-[#F8F1E2] hover:bg-[#F3E7CB] disabled:opacity-60 flex items-center gap-2">
            {busy ? <Spinner size={14} className="animate-spin" /> : text ? <ArrowsClockwise size={14} weight="duotone" /> : <Sparkle size={14} weight="duotone" />}
            {text ? "Re-generate" : "Create Master File"}
          </button>
          <span className="flex-1" />
          <button onClick={onClose} className="text-xs px-3 py-2 rounded text-[#6B6A68]">Close</button>
          <button onClick={pushToSilo} disabled={!text.trim()} data-testid="master-file-push"
            className="text-sm px-4 py-2.5 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-2">
            <PaperPlaneRight size={14} weight="duotone" /> Push to silo
          </button>
        </div>
      </div>

      {pushDialog && (
        <DocSaveDialog
          open={pushDialog}
          onClose={() => setPushDialog(false)}
          content={text}
          sourceSiloId={siloId}
          silos={[{ id: siloId, name: siloName || "this silo" }]}
          lockSilo={true}
          defaultTitle={defaultTitle}
          initialTier={defaultTier}
          dialogTitle="Push Master File to silo"
          onSaved={() => { toast.success("Master file saved into your silo."); onClose(); }}
        />
      )}
    </div>
  );
}
